<template>
  <div data-view="billing">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>
    <layout-logged-frame has-before :title="$t('accounts')">
      <template v-slot:header-actions>
        <header-actions :actions="headerActions">
          <Can
            action="upload"
            subject="billing"
          >
            <base-file-upload
              :trigger-label="$t('upload_csv')"
              accept=".csv"
              outlined
              :on-filter="filterFile"
              :on-change="onUploadFile"
            />
          </Can>
        </header-actions>
      </template>

      <template v-slot:before>
        <NavigationTabs :tabs="navigationTabs"/>
      </template>

      <template v-slot:content>
        <BillingFilters
          @filter="onFilter"
          @clearFilters="onClearFilters"
        >
          <template v-slot:phone-actions>
            <sort-by-select
              :columns="columns"
              :pagination="pagination"
              :on-update-pagination="onUpdatePagination"
            />
          </template>
        </BillingFilters>

        <BillingTable
          :on-add="addAction?.onClick"
          :add-label="addAction?.title"
          :items="billing"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          :on-select-item="selectItem"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import BillingFilters from '../../components/forms/filters/Billing';
  import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import { isStr } from '@/utils';
  import navigationTabs from '../../config/billing/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs'
  import columns from '../../config/billing/columns';
  import BillingTable from '../../components/table/Billing';

  export default {
    mixins: [urlPaginationRestore],
    components: {
      BillingFilters,
      NavigationTabs,
      BillingTable
    },
    data() {
      return {
        columns,
        navigationTabs,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'updated_at',
          totalItems: 0,
          search: ''
        },
        totalPages: 0,
        query: { per_page: 15 },
        isLoading: false,
      };
    },
    computed: {
      addAction() {
        return this.headerActions.find(({ key, isDisabled }) => key === 'add' && !isDisabled);
      },
      headerActions() {
        return [
          this.$ability.can('store', 'billing') && {
            key: 'add',
            title: this.$t('add_new_account'),
            onClick: () => this.$router.push({ name: 'billing-new', query: this.$route.query }),
          },
        ].filter(Boolean);
      },
      billing() {
        return this.$store.getters['billing/items'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
    },
    created() {
      this.getCountries();
      this.getEuCountries();
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getItems(this.query);
      },
      getItems(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store.dispatch('billing/getItems', query)
          .then((res) => {
            const { itemsMeta } = this.$store.getters['billing/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectItem(item) {
        this.$store.dispatch('billing/setActiveItem', item);
        this.$router.push({ name: 'billing-edit', params: { id: item.id }, query: this.$route.query });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy
        };

        this.query = nextQuery;
        this.runInitialFetch(() => this.getItems(nextQuery));
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy
        };

        this.query = nextQuery;
        this.getItems(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.runInitialFetch(() => this.getItems(this.query));
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1 };

        this.getItems(this.query);
      },
      filterFile(file) {
        return !/\.(csv)$/i.test(file.name);
      },
      onUploadFile(file) {
        const query = {
          csv: file
        };

        return this.$store.dispatch('billing/uploadCSV', query);
      },
      getCountries() {
        return this.$store.dispatch('config/getCountries');
      },
      getEuCountries() {
        return this.$store.dispatch('config/getEuCountries');
      },
    },
  };
</script>

<style lang="scss">
  @use '../../assets/styles/theme' as *;

  [data-view="billing"] {
    [data-component="file-upload"] {
      margin: 0 0 0 $spacing-xs;

      .v-btn {
        box-shadow: none;
      }
    }

    .v-datatable {
      min-width: $table-min-width;
    }
  }
</style>
